/**
 * V4.25.0 — "Available Banker" selection (Web) — mirrors the Mobile
 * Available Banker screen for Loan Type B (Outsource).
 *
 * Shown when the loan case status is `bankers_available`: the outsource
 * partner has assigned one or more bankers (`outsource_assignments[]` at
 * status `waiting_agent_pick`) and the agent/customer must now SELECT
 * which bankers to proceed with. Submitting flips the picked assignments
 * → `waiting_banker` and the case → "Waiting Bank".
 *
 * Backend:
 *   GET  /api/public/v1/loan-cases/:id           (returns outsource_assignments[])
 *   POST /api/public/v1/loan-cases/:id/submit-bankers { banker_ids: [assignment_id...] }
 *
 * Deep-link path: `/loan/:id/pick-outsource-bankers`.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { LoanPageHeader } from './_shared';

export default function LoanPickOutsourceBankers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({}); // assignment_id → true
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/public/v1/loan-cases/${id}`);
      const data = r.data?.data || null;
      setCaseData(data);
      // Pre-check any assignments already picked (idempotent re-selection).
      const pre = {};
      (data?.outsource_assignments || []).forEach((a) => {
        if (a.status === 'waiting_banker') pre[a.id] = true;
      });
      setPicked(pre);
    } catch (e) {
      toast.error('Failed to load case');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const toggle = (assignmentId, locked) => {
    if (locked) return;
    setPicked((p) => ({ ...p, [assignmentId]: !p[assignmentId] }));
  };

  const pickedList = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const submit = async () => {
    if (pickedList.length === 0) { toast.error('Select at least one banker'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/public/v1/loan-cases/${id}/submit-bankers`, { banker_ids: pickedList });
      const ns = r.data?.data?.next_screen;
      toast.success(`Submitted to ${pickedList.length} banker${pickedList.length > 1 ? 's' : ''}. Waiting for offers…`);
      navigate(ns === 'loan_offers' ? '/loan-offers' : `/loan/${id}`, { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = detail?.message || detail || 'Submit failed';
      if (Array.isArray(detail?.errors)) {
        msg += ': ' + detail.errors.map((x) => x.message || `${x.field}`).join('; ');
      }
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (loading || !caseData) {
    return <div className="p-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;
  }

  const isReady = caseData.status === 'bankers_available';
  // Only outsource assignments that are awaiting agent pick (selectable)
  // or already picked (locked/checked) are shown here.
  const assignments = (caseData.outsource_assignments || []).filter(
    (a) => a.status === 'waiting_agent_pick' || a.status === 'waiting_banker',
  );

  return (
    <div className="max-w-2xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-pick-outsource-bankers-page">
      <LoanPageHeader caseData={caseData} title="Available Banker" />
      <p className="text-sm text-gray-600">
        The outsource partner{caseData.outsource_partner_name ? ` (${caseData.outsource_partner_name})` : ''} has assigned the
        bankers below. Select the banker(s) you&apos;d like to proceed with — they&apos;ll be notified to submit their offer.
      </p>

      {!isReady && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-800" data-testid="pick-outsource-locked-banner">
          This case is at status <b>{caseData.loan_status_label || caseData.status}</b>. Banker selection is only available at{' '}
          <b>Available Banker</b>.
        </Card>
      )}

      {assignments.length === 0 && (
        <Card className="p-6 text-center text-gray-500" data-testid="pick-outsource-empty">
          No bankers have been assigned yet. The outsource partner will assign bankers shortly — please check back.
        </Card>
      )}

      <div className="space-y-3" data-testid="available-banker-list">
        {assignments.map((a) => {
          const locked = a.status === 'waiting_banker';
          const selected = !!picked[a.id];
          const bankLine = [a.bank, a.branch].filter(Boolean).join(' · ').toUpperCase();
          const score = a.score ?? a.rank_points ?? a.points ?? null;
          return (
            <div
              key={a.id}
              role="button"
              tabIndex={0}
              onClick={() => toggle(a.id, locked)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(a.id, locked); }}
              className={`bg-white border rounded-xl p-4 flex items-center gap-3 transition ${
                selected ? 'border-emerald-500 ring-1 ring-emerald-400' : 'border-gray-200 hover:border-gray-300'
              } ${locked ? 'opacity-90 cursor-default' : 'cursor-pointer'}`}
              data-testid={`available-banker-${a.id}`}
            >
              <div className="flex-1 min-w-0">
                {bankLine && (
                  <p className="text-[11px] font-medium tracking-wide text-gray-400 uppercase">{bankLine}</p>
                )}
                <p className="font-bold text-gray-900 truncate">{a.officer_name || 'Banker'}</p>
                {a.team_leader && (
                  <p className="text-xs text-gray-400 truncate">{a.team_leader}</p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {a.team_leader && (
                    <span className="text-[10px] font-semibold bg-blue-50 text-blue-600 rounded px-1.5 py-0.5">TL</span>
                  )}
                  {locked && (
                    <span className="text-[10px] font-semibold bg-amber-50 text-amber-600 rounded px-1.5 py-0.5">Picked</span>
                  )}
                </div>
              </div>

              {score != null && (
                <div className="text-sm font-bold text-gray-800 bg-gray-100 rounded-lg px-3 py-1.5" data-testid={`available-banker-score-${a.id}`}>
                  {score}
                </div>
              )}

              <div
                className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 border ${
                  selected ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'
                }`}
                data-testid={`available-banker-checkbox-${a.id}`}
                aria-checked={selected}
                role="checkbox"
              >
                {selected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
              </div>
            </div>
          );
        })}
      </div>

      {isReady && assignments.some((a) => a.status === 'waiting_agent_pick') && (
        <div className="sticky bottom-0 pt-2 pb-3 bg-gradient-to-t from-white via-white to-transparent">
          <Button
            onClick={submit}
            disabled={submitting || pickedList.length === 0}
            className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 rounded-xl"
            data-testid="available-banker-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit to {pickedList.length} banker{pickedList.length === 1 ? '' : 's'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}
